import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';

import type { WithChildren } from '@dynatrace/strato-components/core';

import { ScrollContext } from '../contexts/ScrollContext.js';

/**
 * @public
 */
export interface ScrollProviderProps extends WithChildren {
  /** reference to the scrollable container that this context should provide information about.
   * @defaultValue window
   * */
  containerRef?: RefObject<HTMLElement | typeof window | null>;
}

/** continuous id for every ScrollProvider that is created */
let scrollProviderId = 0;
/** global Map to store all scrollPositions before setting them on animation frame */
const scrollPositionMap = new Map();

/**
 * ScrollProvider component that provides scroll information context to it's children
 * @public
 */
export const ScrollProvider = (props: ScrollProviderProps) => {
  const { children } = props;

  // To not use hooks conditionally we need to get the window ref every time. Based on a given
  // containerRef in the props we either choose the containerRef or the windowRef as a fallback.
  const windowRef = useRef(window);
  const containerRef = props.containerRef ?? windowRef;

  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
  const [isScrolling, setIsScrolling] = useState(false);
  const [timeoutId, setTimeoutId] = useState(0);
  const [calculateTick, setCalculateTick] = useState(false);

  // create a name for the scrollProvider component on first render to remember global scrollPosition in Map
  const name = useMemo(
    () => `strato-scroll-provider-${scrollProviderId++}`,
    [],
  );

  // onScroll function that is bound as scroll event
  const onScroll = () => {
    refreshIsScrolling();
    refreshScrollPosition();
  };

  // manages store of component in scrollPosition
  useEffect(() => {
    scrollPositionMap.set(name, scrollPosition);
    return () => {
      scrollPositionMap.delete(name);
    };
  }, [name, scrollPosition]);

  // calculates the scrollPosition but only sets it on animation frame
  const refreshScrollPosition = () => {
    if (containerRef && containerRef.current) {
      if (containerRef.current === window) {
        scrollPositionMap.set(name, { x: window.scrollX, y: window.scrollY });
      } else if (containerRef.current instanceof HTMLElement) {
        scrollPositionMap.set(name, {
          x: containerRef.current.scrollLeft,
          y: containerRef.current.scrollTop,
        });
      }
    }

    if (!calculateTick) {
      window.requestAnimationFrame(() => {
        setScrollPosition(scrollPositionMap.get(name));
        setCalculateTick(false);
      });

      setCalculateTick(true);
    }
  };

  // refreshes isScrolling state
  const refreshIsScrolling = () => {
    window.clearTimeout(timeoutId);
    setIsScrolling(true);

    // store the timeoutID to clear it next run
    // using 66ms for 15fps - there is not noticeable difference if you would go higher.
    // 66ms is a sweet spot that improves performance:
    // https://gomakethings.com/why-66ms-when-using-settimeout/
    setTimeoutId(
      window.setTimeout(() => {
        setIsScrolling(false);
      }, 66),
    );
  };

  // binds and unbinds the onScroll event on the containerRef
  useEffect(() => {
    refreshScrollPosition();
    const node = containerRef?.current;
    if (node) {
      node.addEventListener('scroll', onScroll);
    }

    return () => {
      if (node) {
        node.removeEventListener('scroll', onScroll);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef]);

  return (
    <ScrollContext.Provider
      value={{ scrollPosition, isScrolling }}
      data-testid="scroll-provider"
    >
      {children}
    </ScrollContext.Provider>
  );
};

ScrollProvider.displayName = 'ScrollProvider';
